import {
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewEncapsulation
} from "@angular/core";
import { DomainService } from "../../services/domain.service";
import { IAnalyzerConfig } from "../../interfaces/analyzer-config";
import { AddinTabs, Constants, EvaluationFlowsEnum, LinkDisplayedAsTypes } from "../../models/shared.model";
import { ConfigService } from "../../services/config.service";
import { MailUtils } from "../../utils/mail.utils";
import { ANALYZERS } from "src/app/models/constants";
import { EvaluatorResult } from "../../models/evaluator-result";
import { Subscription } from "rxjs";
import { ScrollToSectionService } from "../../services/scrollToSection.service";
import { KeyValue } from "@angular/common";
import { WaitService } from "../../services/wait.service";
import { HtmlUtils } from "../../utils/html.utils";

interface LinkDisplayedAs {
  displayedAs: LinkDisplayedAsTypes | string;
  imgUrl?: string;
  isHovering: boolean;
  style?: string;
}

interface LinkObject {
  displayedAs: LinkDisplayedAs[],
  trusted: boolean,
  linkClearAndDirect: boolean,
  domainAge: string,
  isDomainNew: boolean,
  secure: boolean,
  linkImageUrl?: string,
  linkIsConditional?: boolean,
  deceptiveLink?: string,
  linkDomain: string
}

interface QRObject {
  trusted: boolean,
  secure: boolean,
  qr_code: string,
  decoded: string,
  isHovering?: boolean,
  domainAge: string,
  isDomainNew: boolean,
  qrCodeDeceptiveLink?: string,
  linkDomain: string
}

enum LinkHtmlIdToLinkObjectParameter {
  linkTrusted = "trusted",
  linkClear = "linkClearAndDirect",
  linkSecure = "secure"
}

@Component({
  selector: "app-links",
  templateUrl: "./links.component.html",
  styleUrls: ["./links.component.css"],
  encapsulation: ViewEncapsulation.None
})
export class LinksComponent implements OnInit, OnDestroy, OnChanges {

  @Input() linksIncomplete = false;
  @Input() qrIncomplete = false;
  @Input("linksResult") linksResult: EvaluatorResult[];
  @Input("qrResult") qrResult: EvaluatorResult[];

  protected readonly EvaluationFlowsEnum = EvaluationFlowsEnum;
  protected readonly Constants = Constants;
  private scrollSubscription!: Subscription;
  private linksFingerprint: string | undefined;
  private qrFingerprint: string | undefined;

  uniqueLinks: Map<string, LinkObject>;
  uniqueQrCodes: Map<string, QRObject>;

  concerningCount = 0;

  isRetrievingAge: boolean[] = new Array(100).fill(false);
  isRetrievingQrCodeLinkAge: boolean[] = new Array(100).fill(false);

  isAnalyzing: boolean = true;

  constructor(
    private domainService: DomainService,
    private configService: ConfigService,
    private el: ElementRef,
    private scrollToSectionService: ScrollToSectionService,
    private waitService: WaitService
  ) {
    this.isAnalyzing = this.waitService.isAnalyzing;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['linksResult']) {
      this.linksResult = changes['linksResult'].currentValue;
      this.processLinks();
    }
    if (changes['qrResult']) {
      this.qrResult = changes['qrResult'].currentValue;
      this.processQRCodes();
    }
  }

  ngOnInit() {

    this.scrollSubscription = this.scrollToSectionService.scrollToSection$.subscribe((scrollTo) => {
      if (scrollTo.tab == AddinTabs.LINKS)
        this.scrollToSection(scrollTo.flowName, scrollTo.id, scrollTo.isSectionReasonForConcern);

    });

    this.waitService.resetData.subscribe((reset) => {
      if (reset) {
        this.linksFingerprint = undefined;
        this.qrFingerprint = undefined;
        this.uniqueLinks = new Map<string, LinkObject>();
        this.uniqueQrCodes = new Map<string, QRObject>();
        this.concerningCount = 0;
      }
    });

    this.waitService.isAnalyzingObserver.subscribe((isAnalyzing) => {
      this.isAnalyzing = isAnalyzing;
    });

    if (this.linksResult)
      this.processLinks();

    if (this.qrResult)
      this.processQRCodes();
  }

  processQRCodes() {
    const fingerprint = JSON.stringify(this.qrResult ?? []);
    if (fingerprint === this.qrFingerprint) return;
    this.qrFingerprint = fingerprint;
    this.uniqueQrCodes = new Map<string, QRObject>();

    this.qrResult?.forEach(item => {
      let info: QRObject = {
        trusted: false,
        secure: false,
        qr_code: item.additionalInfo.qrCode.qr_code,
        decoded: item.additionalInfo.qrCode.decoded,
        isHovering: item.additionalInfo.qrCode.isHovering,
        domainAge: item.additionalInfo.domainAge,
        isDomainNew: item.additionalInfo.isDomainNew,
        qrCodeDeceptiveLink: item.additionalInfo.qrCodeDeceptiveLink,
        linkDomain: MailUtils.parseMultiLevelDomain(item.additionalInfo.qrCode.decoded)
      }

      let currentInfo = this.uniqueQrCodes.get(item.additionalInfo.qrCode.decoded);

      if (currentInfo)
        info = currentInfo;


      if (item.additionalInfo.qrCodeLinkDomainTrusted)
        info.trusted = true;

      if (item.additionalInfo.qrCodeLinkSecure)
        info.secure = true;

      this.uniqueQrCodes.set(item.additionalInfo.qrCode.decoded, info);

    });
  }

  private processLinks() {
    const fingerprint = JSON.stringify(this.linksResult ?? []);
    if (fingerprint === this.linksFingerprint) return;
    this.linksFingerprint = fingerprint;
    this.uniqueLinks = new Map<string, LinkObject>();
    this.concerningCount = 0;

    this.linksResult?.forEach(value => {
      let displayedAs: LinkDisplayedAs[] = [];

      let info: LinkObject = {
        displayedAs: [],
        trusted: false,
        linkClearAndDirect: true,
        domainAge: value.additionalInfo.domainAge,
        isDomainNew: value.additionalInfo.isDomainNew,
        secure: value.additionalInfo.linkSecure,
        linkIsConditional: value.additionalInfo.linkIsConditional,
        deceptiveLink: value.additionalInfo.deceptiveLink,
        linkDomain: MailUtils.parseMultiLevelDomain(value.additionalInfo.link)
      };

      let currentInfo = this.uniqueLinks.get(value.additionalInfo.link);

      if (currentInfo) {
        info = currentInfo;
        displayedAs = this.uniqueLinks.get(value.additionalInfo.link).displayedAs;
      }

      displayedAs.push({
        displayedAs: value.additionalInfo.displayedAs,
        imgUrl: value.additionalInfo.linkImageUrl,
        isHovering: false,
        style: HtmlUtils.convertCSSToInline(value.additionalInfo.linkStyle),
      });

      info.displayedAs = displayedAs;

      if (value.additionalInfo.linkDomainTrusted)
        info.trusted = true;

      if (value.additionalInfo.linkClearAndDirect == false)
        info.linkClearAndDirect = false;


      this.uniqueLinks.set(value.additionalInfo.link, info);

      if (value.additionalInfo.isConcerning == true)
        this.concerningCount++;
    });
  }

  retrieveDomainAge(flow: EvaluationFlowsEnum, link: string, linkInfo: LinkObject | QRObject, idx: number, isRetrieving: boolean[]) {

    if (isRetrieving[idx])
      return;

    isRetrieving[idx] = true;


    try {
      this.domainService.getDomainRegisteredDate(linkInfo.linkDomain).then(value => {
        if(value == undefined) {
          linkInfo.domainAge = Constants.NOT_AVAILABLE_DOMAIN_AGE;
          linkInfo.isDomainNew = undefined;
          this.updateMatchingEntries(flow, linkInfo);
          return;
        }
        else if (!value) {
          linkInfo.domainAge = Constants.FAILED_DOMAIN_AGE;
          linkInfo.isDomainNew = true;
          this.updateMatchingEntries(flow, linkInfo);
          return;
        }
        const config: IAnalyzerConfig = this.configService.getAnalyzerConfiguration(ANALYZERS.DOMAIN_AGE);

        const domainAge = this.domainService.calculateDomainAge(value);

        linkInfo.domainAge = domainAge.formattedDate;
        linkInfo.isDomainNew = domainAge.months <= config.thresholds.high;
        this.updateMatchingEntries(flow, linkInfo);

      }).finally(() => {
        isRetrieving[idx] = false;

      });
    } catch (e) {
      linkInfo.domainAge = Constants.FAILED_DOMAIN_AGE;
      linkInfo.isDomainNew = true;
      this.updateMatchingEntries(flow, linkInfo);
      isRetrieving[idx] = false;
    }
  }
  updateMatchingEntries(flow: EvaluationFlowsEnum, linkInfo: LinkObject | QRObject) {
    if (flow == EvaluationFlowsEnum.LINKS)
    for (const [key, linkObject] of this.uniqueLinks.entries()) {
      if (linkObject.linkDomain === linkInfo.linkDomain) {
          this.uniqueLinks.set(key, { ...linkObject, domainAge: linkInfo.domainAge, isDomainNew: linkInfo.isDomainNew });
      }
    }
    else if(flow == EvaluationFlowsEnum.QRCODE)
      for (const [key, linkObject] of this.uniqueQrCodes.entries()) {
      if (linkObject.linkDomain === linkInfo.linkDomain) {
          this.uniqueQrCodes.set(key, { ...linkObject, domainAge: linkInfo.domainAge, isDomainNew: linkInfo.isDomainNew });
      }
    }
  }

  getLinkDisplayStyle(linkDisplay: LinkDisplayedAs) {
    const ignoreStyles = [LinkDisplayedAsTypes.FULL_URL.toString(), LinkDisplayedAsTypes.IMAGE.toString(), LinkDisplayedAsTypes.BUTTON.toString()]

    if(ignoreStyles.includes(linkDisplay.displayedAs))
      return null;

    return `${linkDisplay.style || ''}; max-width: 100% !important; white-space: normal !important; word-break: break-word !important; overflow-wrap: break-word !important;`;
  }

  getDomainFromLink(item: KeyValue<string, any>, flow: EvaluationFlowsEnum) {
    if(flow == EvaluationFlowsEnum.LINKS) {
      const url = item.value?.deceptiveLink ?? this.cleanUrl(item.key);
      return MailUtils.parseMultiLevelDomain(url);
    }

    const url = item.value?.qrCodeDeceptiveLink ?? this.cleanUrl(item.value?.decoded);
    return MailUtils.parseMultiLevelDomain(url);
  }

  /**
   * This functions uses regx to extracts the link and remove query params and # and /
   * @param url
   */
  cleanUrl(url: string){
    const exactUrl = url.match(/^[a-z][\w+.-]*:\/\/([^\/?#]+)/i);
    if(exactUrl && exactUrl.length > 0){
      return exactUrl[0];
    }
    return url;
  }

  // Sort asc
  keyValueComparator = (a: KeyValue<string, LinkObject>, b: KeyValue<string, LinkObject>): number => {
    const aIsDeceptive = !!a.value.deceptiveLink;
    const bIsDeceptive = !!b.value.deceptiveLink;

    if (aIsDeceptive && !bIsDeceptive) return -1;
    if (!aIsDeceptive && bIsDeceptive) return 1;

    if (aIsDeceptive && bIsDeceptive) {
      // sort on deceptiveLink asc
      return a.value.deceptiveLink!.localeCompare(b.value.deceptiveLink!);
    }

    // default sort on link asc
    return a.key.localeCompare(b.key);
  };

  // Sort asc
  keyValueComparatorQrCodes = (a: KeyValue<string, QRObject>, b: KeyValue<string, QRObject>): number => {
    const aIsDeceptive = !!a.value.qrCodeDeceptiveLink;
    const bIsDeceptive = !!b.value.qrCodeDeceptiveLink;

    if (aIsDeceptive && !bIsDeceptive) return -1;
    if (!aIsDeceptive && bIsDeceptive) return 1;

    if (aIsDeceptive && bIsDeceptive) {
      // sort on qrCodeDeceptiveLink asc
      return a.value.qrCodeDeceptiveLink!.localeCompare(b.value.qrCodeDeceptiveLink!);
    }

    // default sort on link asc
    return a.key.localeCompare(b.key);
  };

  // This fn used to sort the Map in the same way they displayed in the html
  getSortedEntries(map: Map<string, LinkObject>): Array<{ key: string; value: LinkObject }> {
    return Array.from(map.entries())
      .sort(([keyA], [keyB]) => keyA.localeCompare(keyB))
      .map(([key, value]) => ({ key, value }));
  }

  // This fn used to sort the Map in the same way they displayed in the html
  getSortedEntriesQrCodes(map: Map<string, QRObject>): Array<{ key: string; value: QRObject }> {
    return Array.from(map.entries())
      .sort(([keyA], [keyB]) => keyA.localeCompare(keyB))
      .map(([key, value]) => ({ key, value }));
  }

  scrollToSection(flowName: string, sectionId: string, isSectionReasonForConcern: boolean) {
    let idOfLink = "";
    let list = null;

    if(flowName == EvaluationFlowsEnum.LINKS)
      list = this.getSortedEntries(this.uniqueLinks);
    else if(flowName == EvaluationFlowsEnum.QRCODE)
      list = this.getSortedEntriesQrCodes(this.uniqueQrCodes)

    for (let value of list) {
      if (value.value[LinkHtmlIdToLinkObjectParameter[sectionId]] != isSectionReasonForConcern) { // Condition to meet
        idOfLink = value.key;
        break;
      }
    }

    this.highlightSection(sectionId, idOfLink);
  }

  highlightSection(sectionId: string, itemId: string){
    const targetElement = this.el.nativeElement.querySelector(`[id="${sectionId}-${itemId}"]`);

    if (targetElement) {
      setTimeout(() => {
        targetElement.scrollIntoView({ behavior: "smooth", block: "center" });
        this.scrollToSectionService.applyHighlight(targetElement);
      }, 200);
    }
  }

  ngOnDestroy() {
    this.scrollSubscription.unsubscribe();
  }

  getDomainAgeColor(isDomainNew: boolean | undefined) {
    if(isDomainNew == undefined)
      return 'text-orange-color';
    if(isDomainNew)
      return 'red';
    return 'text-green-2-color';
  }
}

